/**
 * TOSS Web Application - Notifications Panel Component
 *
 * The panel that drops out of the bell in the internal header. Since 2026-08-26 the panel itself
 * is `NotificationsPopover` from the shared top-bar layer — the customer portal drops the same one
 * out of its bell (owner: «сделать как в клиентском кабинете»).
 *
 * What changed with the move is the SHAPE. This panel used to draw `NotificationItem`: a 40 px
 * round type icon, a coloured type badge and 16 px of padding per row, so five notifications
 * filled a panel where the cabinet fits eight and still has room. What it drew has no counterpart
 * in the portal, and neither surface was better for it.
 *
 * What did NOT change is everything the panel does: it fetches only while open, marks one read and
 * navigates on a click, marks all read from its header and leads to the full page from its foot —
 * pinned by `NotificationsPanel.test.tsx`, written before the move.
 *
 * This file is the internal HALF of the shared panel: it turns `Notification` into rows of three
 * strings and owns what a click does. The shared component never sees a notification — the two
 * DTOs behind those rows are not interchangeable.
 *
 * Since 24.09.2026 it also owns the chip row «Все / Важные / Непрочитанные» (owner, variant C1 of
 * the priority split): one feed in time order, the chips narrow which eight rows are drawn. The
 * counts come from the same unread query as the bell, so the red number on the bell and the one on
 * «Важные» are always the same number.
 */

import { useCallback, useMemo, useState, type RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUIStore } from '@/store/uiStore';
import {
  useNotifications,
  useMarkAllAsRead,
  useMarkAsRead,
  useUnreadCount,
} from '@/hooks/api/useNotifications';
import { ROUTES } from '@/constants';
import {
  NotificationsPopover,
  NOTIFICATIONS_POPOVER_LIMIT,
  type NotificationRowView,
} from '@/components/topbar';
import type { NotificationListRequest } from '@/api/notifications.api';
import { notificationMeta, notificationTarget } from '../notificationPresentation';

// ============================================================================
// Types
// ============================================================================

export interface NotificationsPanelProps {
  /** Reference to the trigger element for positioning */
  triggerRef: RefObject<HTMLElement | null>;
}

type PanelFilter = 'all' | 'priority' | 'unread';

/** What each chip asks the server for. */
const FILTER_PARAMS: Record<PanelFilter, Pick<NotificationListRequest, 'isPriority' | 'isRead'>> = {
  all: {},
  priority: { isPriority: true },
  unread: { isRead: false },
};

const EMPTY_TEXT: Record<PanelFilter, string> = {
  all: 'Уведомлений пока нет',
  priority: 'Важных уведомлений нет',
  unread: 'Непрочитанных нет',
};

/**
 * Shown in place of the empty text when the list request failed. Before 30.09.2026 a failure read
 * «Важных уведомлений нет» — a lie that looks exactly like the feature not working.
 */
const ERROR_TEXT = 'Не удалось загрузить уведомления';

// ============================================================================
// Component
// ============================================================================

/**
 * Notifications panel dropdown component
 *
 * @example
 * ```tsx
 * const triggerRef = useRef<HTMLButtonElement>(null);
 * <NotificationsPanel triggerRef={triggerRef} />
 * ```
 */
export function NotificationsPanel({ triggerRef }: NotificationsPanelProps): JSX.Element {
  const navigate = useNavigate();

  const isOpen = useUIStore((state) => state.isNotificationsPanelOpen);
  const closePanel = useUIStore((state) => state.closeNotificationsPanel);

  // «Все» by default — the feed in time order, red dots where it matters. Kept while the panel is
  // closed and reopened within the session; nobody asked for it to survive a reload.
  const [filter, setFilter] = useState<PanelFilter>('all');

  // One page, exactly as many rows as the panel draws — it used to ask for five and draw five,
  // which is where the «panel holds less than the cabinet's» impression started.
  const { data, isLoading, isError } = useNotifications(
    { page: 1, pageSize: NOTIFICATIONS_POPOVER_LIMIT, ...FILTER_PARAMS[filter] },
    { enabled: isOpen }
  );
  const { data: unread } = useUnreadCount();

  const markAllAsRead = useMarkAllAsRead();
  const markAsRead = useMarkAsRead();

  // Memoised because `activate` closes over it: a fresh `[]` on every render would give the
  // shared panel a new callback each time and defeat its own memoisation.
  const notifications = useMemo(() => data?.items ?? [], [data]);

  // From the counter, not from the eight rows on screen: under «Важные» every visible row may be
  // read while forty ordinary ones are not, and «Прочитать все» must still be offered.
  const unreadTotal = unread?.count ?? 0;
  const hasUnread = unreadTotal > 0 || notifications.some((notification) => !notification.isRead);

  const items: NotificationRowView[] = notifications.map((notification) => ({
    id: notification.id,
    title: notification.title,
    meta: notificationMeta(notification),
    isRead: notification.isRead,
    isPriority: notification.isPriority ?? false,
  }));

  const filters = {
    value: filter,
    onChange: (id: string) => setFilter(id as PanelFilter),
    options: [
      { id: 'all', label: 'Все' },
      { id: 'priority', label: 'Важные', count: unread?.priorityCount ?? 0 },
      { id: 'unread', label: 'Непрочитанные', count: unreadTotal },
    ],
  };

  const activate = useCallback(
    (id: string) => {
      const notification = notifications.find((candidate) => candidate.id === id);
      if (!notification) return;

      if (!notification.isRead) markAsRead.mutate(notification.id);

      const target = notificationTarget(notification);
      if (target) navigate(target);

      closePanel();
    },
    [notifications, markAsRead, navigate, closePanel]
  );

  const handleMarkAllAsRead = useCallback(() => {
    markAllAsRead.mutate();
  }, [markAllAsRead]);

  return (
    <NotificationsPopover
      isOpen={isOpen}
      onClose={closePanel}
      triggerRef={triggerRef}
      items={items}
      isLoading={isLoading}
      hasUnread={hasUnread}
      onMarkAll={handleMarkAllAsRead}
      isMarkingAll={markAllAsRead.isPending}
      onActivate={activate}
      allHref={ROUTES.notifications.list}
      filters={filters}
      emptyText={isError ? ERROR_TEXT : EMPTY_TEXT[filter]}
    />
  );
}

export default NotificationsPanel;
